import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Building2, FileSpreadsheet, Check, X, Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { t, getHeading, getFooterDescription } from '../translations';
import { formatPrice } from '../utils/localeConfig';
import LanguageSwitcher from './common/LanguageSwitcher';
import CountrySelector from './common/CountrySelector';
import SEOHead from './common/SEOHead';
import {
  generateFingerprint,
  checkFingerprintUsed,
  createAnonymousSession
} from '../utils/anonymousSession';

const LandingPage = ({ forceCountry }) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { country, locale, language, loading, forceSetCountry } = useLocale();

  // Force country for SEO routes (e.g. /us, /ca, /uk)
  useEffect(() => {
    if (forceCountry && country !== forceCountry) {
      forceSetCountry(forceCountry);
    }
  }, [forceCountry, country, forceSetCountry]);

  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  // Debug logging for geolocation detection
  useEffect(() => {
    if (locale && country) {
      console.log('╔═══════════════════════════════════════════════════════════════╗');
      console.log('║                  🌍 GEOLOCATION DETECTED                      ║');
      console.log('╚═══════════════════════════════════════════════════════════════╝');
      console.log('📍 Country Code:', country);
      console.log('🌐 Language:', language);
      console.log('💰 Currency:', `${locale.currency.symbol} ${locale.currency.code}`);
      console.log('💵 Price per page:', formatPrice(locale.pricePerPage, locale));
      console.log('🏦 Banks:', locale.banks);
      console.log('📅 Default date format:', locale.defaultDateFormat);
      console.log('═══════════════════════════════════════════════════════════════');
    }
  }, [locale, country, language]);

  // Handle "Try For Free" button click
  const handleTryForFree = async () => {
    try {
      // SECURITY FIX: Always logout any existing user first to prevent
      // loading previous user's data on shared computers/browsers
      console.log('🔒 SECURITY: Checking for existing session...');

      if (currentUser) {
        console.log('🔒 SECURITY: Existing session detected. Logging out before creating anonymous session...');
        await logout();
        console.log('✅ SECURITY: Existing session cleared');

        // Wait a moment for Firebase Auth state to update
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        console.log('✅ SECURITY: No existing session. Safe to proceed.');
      }

      // Generate fingerprint
      const fingerprint = generateFingerprint();
      console.log('🔍 Generated fingerprint:', fingerprint);

      // Check if fingerprint has been used before
      const hasUsed = await checkFingerprintUsed(fingerprint);
      console.log('📊 Fingerprint check result:', hasUsed ? 'USED' : 'AVAILABLE');

      if (hasUsed) {
        // Already used anonymous session - redirect to registration
        console.warn('⚠️ Fingerprint already used. Redirecting to registration.');
        alert('You have already used your free trial. Please register to continue.');
        navigate('/register');
        return;
      }

      // Create new anonymous session
      console.log('✅ Creating new anonymous session...');
      await createAnonymousSession(fingerprint);

      // Redirect to dashboard
      console.log('🎯 Redirecting to dashboard with anonymous session');
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Error creating anonymous session:', error);

      // Show specific error message based on error code
      let errorMessage = 'Failed to start free trial. ';

      if (error.code === 'auth/operation-not-allowed' || error.code === 'auth/admin-restricted-operation') {
        errorMessage += 'Anonymous authentication is not enabled. Please contact support.';
        console.error('🚨 FIREBASE CONFIG ERROR: Anonymous Auth must be enabled in Firebase Console > Authentication > Sign-in method');
        console.error('🔗 Enable it here: https://console.firebase.google.com/project/pdf-statement-converter-net/authentication/providers');
      } else if (error.code === 'permission-denied' || error.code?.includes('permission')) {
        errorMessage += 'Permission denied. Please try again or contact support.';
        console.error('🚨 FIRESTORE RULES ERROR: Check anonymousSessions collection permissions');
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again or contact support.';
      }

      alert(errorMessage);
    }
  };

  // Show loading skeleton while detecting location
  if (loading || !locale) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <FileSpreadsheet className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead country={country} />

      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900">PDF STATEMENT CONVERTER</span>
              <span className="text-xs text-gray-500">Boone & Boo</span>
            </div>
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            <a href="#pricing" className="text-gray-600 hover:text-gray-900">
              {t(language, 'header.pricing')}
            </a>
            <Link to={forceCountry ? `/${({US:'us',CA:'ca',GB:'uk',BR:'br',AR:'ar',AE:'ae',ZA:'za'})[forceCountry]}/blog` : '/blog'} className="text-gray-600 hover:text-gray-900">
              {t(language, 'header.blog')}
            </Link>
            <Link to="/login" className="text-gray-600 hover:text-gray-900">
              {t(language, 'header.login')}
            </Link>
            <Link to="/register" className="text-gray-600 hover:text-gray-900">
              {t(language, 'header.register')}
            </Link>
            <button
              onClick={openContactModal}
              className="text-gray-600 border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50 transition-colors"
            >
              {t(language, 'header.contact')}
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {getHeading(language, country)}
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t(language, 'hero.subtitle')}
          </p>

          {/* Action Buttons */}
          <div className="mb-16 flex flex-col sm:flex-row gap-4 justify-center items-center">
            {/* YouTube button - Commented out for future use
            <a
              href="https://www.youtube.com/watch?v=dqELsOuyUT8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-white border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3 group-hover:bg-green-700 transition-colors">
                <Play className="w-4 h-4 text-white ml-0.5" />
              </div>
              <span className="font-semibold">{t(language, 'hero.watchVideo')}</span>
            </a>
            */}

            {/* Try For Free Button */}
            <button
              onClick={handleTryForFree}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 hover:shadow-lg transition-all duration-300 font-semibold"
            >
              {t(language, 'hero.tryFree')}
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t(language, 'features.privacyTitle')}</h3>
              <p className="text-gray-600">{t(language, 'features.privacyDesc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t(language, 'features.banksTitle')}</h3>
              <p className="text-gray-600">{t(language, 'features.banksDesc')} {locale.banks}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t(language, 'features.readyTitle')}</h3>
              <p className="text-gray-600">{t(language, 'features.readyDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">{t(language, 'pricing.title')}</h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Pay Per Use */}
            <div className="border-2 border-gray-200 hover:border-green-300 transition-colors rounded-lg overflow-hidden">
              <div className="text-center p-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">{t(language, 'pricing.payPerUse.title')}</h3>

                <div className="mt-4">
                  <span className="text-3xl font-bold text-gray-900">{formatPrice(locale.pricePerPage, locale)}</span>
                  <span className="text-gray-600"> {t(language, 'pricing.payPerUse.perPage')}</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">{t(language, 'pricing.payPerUse.instantConversion')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">{t(language, 'pricing.payPerUse.registrationRequired')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">{t(language, 'pricing.payPerUse.secureProcessing')}</span>
                </div>
                <Link to="/login">
                  <button className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md">
                    {t(language, 'pricing.payPerUse.button')}
                  </button>
                </Link>
              </div>
            </div>

            {/* Credit Packs */}
            <div className="border-2 border-green-300 hover:border-green-400 transition-colors rounded-lg overflow-hidden relative pt-4">
              <div className="absolute -top-6S left-1/2 transform -translate-x-1/2 bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md z-10">
                {t(language, 'pricing.creditPacks.bestValue')}
              </div>
              <div className="text-center p-6 pt-8 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">{t(language, 'pricing.creditPacks.title')}</h3>
                <p className="text-gray-600 text-sm">{t(language, 'pricing.creditPacks.subtitle')}</p>
                <div className="mt-4 space-y-2">
                  <div>
                    <span className="text-lg font-semibold text-gray-900">
                      25 {t(language, 'pricing.creditPacks.credits')}: {formatPrice(locale.bulkPricing[25], locale)}
                    </span>
                    <span className="text-sm text-gray-600 block">
                      ({formatPrice(locale.bulkPricing[25] / 25, locale)} {t(language, 'pricing.creditPacks.perPageSuffix')})
                    </span>
                  </div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900">
                      100 {t(language, 'pricing.creditPacks.credits')}: {formatPrice(locale.bulkPricing[100], locale)}
                    </span>
                    <span className="text-sm text-gray-600 block">
                      ({formatPrice(locale.bulkPricing[100] / 100, locale)} {t(language, 'pricing.creditPacks.perPageSuffix')})
                    </span>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded p-2 border border-orange-200">
                    <span className="text-lg font-bold text-orange-800">
                      500 {t(language, 'pricing.creditPacks.credits')}: {formatPrice(locale.bulkPricing[500], locale)}
                    </span>
                    <span className="text-sm text-orange-700 block">
                      ({formatPrice(locale.bulkPricing[500] / 500, locale)} {t(language, 'pricing.creditPacks.perPageSuffix')} - {t(language, 'pricing.creditPacks.bestValueSuffix')})
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">{t(language, 'pricing.creditPacks.neverExpire')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">{t(language, 'pricing.creditPacks.priorityProcessing')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <span className="text-gray-600">{t(language, 'pricing.creditPacks.bulkDiscounts')}</span>
                </div>
                <Link to="/login">
                  <button className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md">
                    {t(language, 'pricing.creditPacks.button')}
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Promo Code CTA */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Have a promo code?{' '}
              <Link to="/register" className="text-green-600 hover:text-green-500 font-medium">
                Sign up here to use it!
              </Link>
            </p>
            <div className="text-sm text-gray-500">
              Get bonus credits when you register with a valid promo code
            </div>
          </div>
        </div>
      </section>

 

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
              <FileSpreadsheet className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">PDF Statement Converter</span>
          </div>
          <p className="text-gray-600 mb-4">{getFooterDescription(language, country)}</p>
          <div className="flex justify-center space-x-6 text-sm text-gray-500 mb-6">
            <Link to="/privacy-policy" className="hover:text-gray-700">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-gray-700">
              Terms of Service
            </Link>
            <button onClick={openContactModal} className="hover:text-gray-700">
              Contact
            </button>
          </div>

          {/* Country Pages */}
          <div className="text-sm text-gray-500 mb-4">
            Available for:{' '}
            <a href="/us" className="hover:text-gray-700">US</a> |{' '}
            <a href="/ca" className="hover:text-gray-700">Canada</a> |{' '}
            <a href="/uk" className="hover:text-gray-700">UK</a> |{' '}
            <a href="/br" className="hover:text-gray-700">Brazil</a> |{' '}
            <a href="/ar" className="hover:text-gray-700">Argentina</a> |{' '}
            <a href="/ae" className="hover:text-gray-700">UAE</a> |{' '}
            <a href="/za" className="hover:text-gray-700">South Africa</a>
          </div>

          {/* Country and Language Selectors */}
          <div className="flex justify-center items-center space-x-4 pt-4 border-t border-gray-200">
            <CountrySelector />
            <LanguageSwitcher />
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeContactModal}>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeContactModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Us</h2>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-gray-600 mb-2">Email us at:</p>
                <a
                  href="mailto:admin@pdfstatementconverter.co.za"
                  className="text-green-600 hover:text-green-800 font-semibold text-lg"
                >
                  admin@pdfstatementconverter.co.za
                </a>
              </div>
              <button
                onClick={closeContactModal}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;